import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Id, Version } from '@contracts/common';
import type {
  ContractCreateBody, ContractDTO, ContractPatchBody, ParticipantCreateBody, ParticipantDTO, ParticipantPatchBody,
  ProjectCreateBody, ProjectDetailDTO, ProjectPatchBody, ProjectStakeholdersDTO, ProjectStatus, ProjectSummaryDTO,
  StakeholderCreateBody, StakeholderDTO, StakeholderPatchBody,
} from '@contracts/projects';
import type { SharingEffect } from '@contracts/sharing';
import { api } from '../../api/client';
import { usePreservedMutation } from '../../api/query';

/**
 * The projects' reads and writes (projects & stakeholders spec §3.1, §5.3). Every write answers with the project as it
 * now stands, so the detail is written straight into its cache and the lists are re-read.
 */

export type ProjectFilter = {
  /** The statuses shown; empty shows every status. */
  status: readonly ProjectStatus[];
  q?: string;
};

/** `/api/projects?status=…&q=…`, empty parts left out, in that order. */
export function projectsPath({ status, q = '' }: ProjectFilter): string {
  const parts: string[] = [];
  if (status.length > 0) parts.push(`status=${status.join(',')}`);
  if (q.trim() !== '') parts.push(`q=${encodeURIComponent(q.trim())}`);
  return parts.length === 0 ? '/api/projects' : `/api/projects?${parts.join('&')}`;
}

export const PROJECTS_KEY = ['projects'] as const;
export const projectKey = (id: Id) => ['project', id] as const;

export function useProjects(filter: ProjectFilter, enabled = true) {
  const path = projectsPath(filter);
  return useQuery({
    queryKey: [...PROJECTS_KEY, path],
    queryFn: () => api<ProjectSummaryDTO[]>(path),
    enabled,
    // A new search keeps the rows on screen until its answer arrives, so the table never flashes empty.
    placeholderData: keepPreviousData,
  });
}

export function useProject(id: Id, enabled = true) {
  return useQuery({
    queryKey: projectKey(id), queryFn: () => api<ProjectDetailDTO>(`/api/projects/${id}`),
    enabled: enabled && id !== '', retry: false,
  });
}

/** Where a project's stakeholders are cached. */
export const stakeholdersKey = (id: Id) => [...projectKey(id), 'stakeholders'] as const;

/** The project's stakeholders and their participants (spec §5.3): the Stakeholder tab and the item's picker. */
export function useProjectStakeholders(id: Id | null) {
  return useQuery({
    queryKey: stakeholdersKey(id ?? ''),
    queryFn: () => api<ProjectStakeholdersDTO>(`/api/projects/${id}/stakeholders`),
    enabled: id !== null, retry: false,
  });
}

/** Writes the answer into the detail's cache and has the lists re-read. */
function useStore() {
  const queryClient = useQueryClient();
  return useCallback((detail: ProjectDetailDTO | null, gone?: Id) => {
    if (detail !== null) queryClient.setQueryData(projectKey(detail.id), detail);
    // Removed, not re-read: the page showing it is leaving, and a re-read would only answer 404.
    if (gone !== undefined) queryClient.removeQueries({ queryKey: projectKey(gone) });
    void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  }, [queryClient]);
}

export function useCreateProject() {
  const store = useStore();
  return usePreservedMutation(useCallback(async (body: Required<ProjectCreateBody>, requestId: Id) => {
    const answer = await api<ProjectDetailDTO & Partial<SharingEffect>>('/api/projects', { method: 'POST', body, requestId });
    const { addedToSharing = [], ...detail } = answer;
    store(detail);
    return { ...detail, addedToSharing };
  }, [store]));
}

export function useUpdateProject(id: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (body: ProjectPatchBody, requestId: Id) => {
    const answer = await api<ProjectDetailDTO & Partial<SharingEffect>>(`/api/projects/${id}`, { method: 'PATCH', body, requestId });
    const { addedToSharing = [], ...detail } = answer;
    store(detail);
    return { ...detail, addedToSharing };
  }, [id, store]));
}

export function useDeleteProject(id: Id) {
  const store = useStore();
  return usePreservedMutation(useCallback(async (version: Version, requestId: Id) => {
    const answer = await api<{ id: Id }>(`/api/projects/${id}`, { method: 'DELETE', body: { version }, requestId });
    store(null, id);
    return answer;
  }, [id, store]));
}

/*
 * The stakeholder writes (spec §5.3, plan Task 10). Each answers with the one row it changed — a stakeholder, a person
 * or a contract — not the whole list, so the answer is put into the cached list in place, and the project detail is
 * re-read for its header counts ("6 tổ chức · 11 người · ▲ 3 cần tác động") and the lists for their columns.
 */

type Rows = StakeholderDTO[];
type Change = (rows: Rows) => Rows;

function useStakeholderStore(projectId: Id) {
  const queryClient = useQueryClient();
  return useCallback((change: Change) => {
    queryClient.setQueryData<ProjectStakeholdersDTO>(stakeholdersKey(projectId), (was) => (
      was === undefined ? was : { ...was, stakeholders: change(was.stakeholders) }));
    void queryClient.invalidateQueries({ queryKey: projectKey(projectId), exact: true });
    void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
  }, [queryClient, projectId]);
}

/** `rows` with stakeholder `sid` changed by `change` (removed when it answers null). */
const onRow = (sid: Id, change: (row: StakeholderDTO) => StakeholderDTO | null): Change => (rows) =>
  rows.flatMap((row) => {
    if (row.id !== sid) return [row];
    const next = change(row);
    return next === null ? [] : [next];
  });

const base = (projectId: Id) => `/api/projects/${projectId}/stakeholders`;

export function useAddStakeholder(projectId: Id) {
  const store = useStakeholderStore(projectId);
  return usePreservedMutation(useCallback(async (body: StakeholderCreateBody, requestId: Id) => {
    const row = await api<StakeholderDTO>(base(projectId), { method: 'POST', body, requestId });
    store((rows) => [...rows.filter((entry) => entry.id !== row.id), row]);
    return row;
  }, [projectId, store]));
}

export function useUpdateStakeholder(projectId: Id) {
  const store = useStakeholderStore(projectId);
  return usePreservedMutation(useCallback(async ({ sid, body }: { sid: Id; body: StakeholderPatchBody }, requestId: Id) => {
    const row = await api<StakeholderDTO>(`${base(projectId)}/${sid}`, { method: 'PATCH', body, requestId });
    store(onRow(sid, () => row));
    return row;
  }, [projectId, store]));
}

export function useRemoveStakeholder(projectId: Id) {
  const store = useStakeholderStore(projectId);
  return usePreservedMutation(useCallback(async ({ sid, version }: { sid: Id; version: Version }, requestId: Id) => {
    const answer = await api<{ id: Id }>(`${base(projectId)}/${sid}`, { method: 'DELETE', body: { version }, requestId });
    store(onRow(sid, () => null));
    return answer;
  }, [projectId, store]));
}

export type ParticipantWrite =
  | { kind: 'add'; sid: Id; body: ParticipantCreateBody }
  | { kind: 'update'; sid: Id; pid: Id; body: ParticipantPatchBody }
  | { kind: 'remove'; sid: Id; pid: Id; version: Version };

/** A person of a stakeholder added, changed or removed; `pid` is the participant row's id, not the contact's. */
export function useParticipantWrite(projectId: Id) {
  const store = useStakeholderStore(projectId);
  return usePreservedMutation(useCallback(async (write: ParticipantWrite, requestId: Id) => {
    const path = `${base(projectId)}/${write.sid}/contacts`;
    if (write.kind === 'remove') {
      await api<{ id: Id }>(`${path}/${write.pid}`, { method: 'DELETE', body: { version: write.version }, requestId });
      store(onRow(write.sid, (row) => ({ ...row, participants: row.participants.filter((p) => p.id !== write.pid) })));
      return null;
    }
    const person = write.kind === 'add'
      ? await api<ParticipantDTO>(path, { method: 'POST', body: write.body, requestId })
      : await api<ParticipantDTO>(`${path}/${write.pid}`, { method: 'PATCH', body: write.body, requestId });
    store(onRow(write.sid, (row) => ({
      ...row,
      participants: row.participants.some((p) => p.id === person.id)
        ? row.participants.map((p) => (p.id === person.id ? person : p))
        : [...row.participants, person],
    })));
    return person;
  }, [projectId, store]));
}

export type ContractWrite =
  | { kind: 'add'; sid: Id; body: ContractCreateBody }
  | { kind: 'update'; sid: Id; kid: Id; body: ContractPatchBody }
  | { kind: 'remove'; sid: Id; kid: Id; version: Version };

export function useContractWrite(projectId: Id) {
  const store = useStakeholderStore(projectId);
  return usePreservedMutation(useCallback(async (write: ContractWrite, requestId: Id) => {
    const path = `${base(projectId)}/${write.sid}/contracts`;
    if (write.kind === 'remove') {
      await api<{ id: Id }>(`${path}/${write.kid}`, { method: 'DELETE', body: { version: write.version }, requestId });
      store(onRow(write.sid, (row) => ({ ...row, contracts: row.contracts.filter((k) => k.id !== write.kid) })));
      return null;
    }
    const contract = write.kind === 'add'
      ? await api<ContractDTO>(path, { method: 'POST', body: write.body, requestId })
      : await api<ContractDTO>(`${path}/${write.kid}`, { method: 'PATCH', body: write.body, requestId });
    store(onRow(write.sid, (row) => ({
      ...row,
      contracts: row.contracts.some((k) => k.id === contract.id)
        ? row.contracts.map((k) => (k.id === contract.id ? contract : k))
        : [...row.contracts, contract],
    })));
    return contract;
  }, [projectId, store]));
}
